import React, {
    ReactElement, useMemo, useState,
} from "react";
import { Dropdown } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { BiCheckSquare, BiSquareRounded } from "react-icons/bi";
import { GrFormFilter } from "react-icons/gr";
import { TbTemplate } from "react-icons/tb";
import { Loading } from "../../../../components";
import { CUSTOM_FIELD_PROPS } from "../../../../components/pickers/field-type/FieldType";
import useRouting, { Link } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";
import {
    GetRequestTypesDocument, useCloneFormMutation, useGetRequestTypesQuery, UserGroup,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import { ListWithGroups } from "../../../../components/list-with-groups/ListWithGroups";
import { JOB_FUNCTION_NAME } from "../request-form/RequestForm";
import Avatar from "../../../../components/avatar/Avatar";
import usePortal from "../../../../contexts/PortalContext";

const FEATURE_TEMPLATES = "frontend.request-type.templates";
interface FilterProps {
    groups: Pick<UserGroup, "id" | "name">[];
    selectedGroups: Set<string>;
    onSelected: (ids: Set<string>) => void;
    search: string;
    onSetSearch: (value: string) => void;
}

function Filters({
    groups, selectedGroups, onSelected, search, onSetSearch,
}: FilterProps): ReactElement {
    return (
        <div className="d-flex mb-1">
            {groups.isNotEmpty && (
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                        variant=""
                        className="menu-small p-1"
                    >
                        <GrFormFilter />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {groups.map(({ id, name }) => (
                            <Dropdown.Item
                                key={id}
                                eventKey={id}
                                onClick={() => {
                                    if (selectedGroups.has(id)) {
                                        selectedGroups.delete(id);
                                    } else {
                                        selectedGroups.add(id);
                                    }

                                    onSelected(new Set(Array.from(selectedGroups).union(groups.toIdList("id"))));
                                }}
                            >
                                {selectedGroups.has(id) ? <BiCheckSquare /> : <BiSquareRounded />}
                                {" "}
                                {name}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            )}

            <input
                className="no-border ms-2"
                type="search"
                placeholder="Search..."
                value={search}
                onChange={evt => onSetSearch(evt.target.value)}
            />
        </div>
    );
}

export default function RequestTypes(): ReactElement {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        fetchPolicy: "no-cache",
    });
    const { Paths, historyPush } = useRouting();
    const userSession = useCurrentSession();

    const { featureFlags } = usePortal();

    const [fieldFilters, setFieldFilters] = useState<Set<string>>(() => new Set(userSession.user?.userGroups.toIdList("id")));
    const [fieldSearch, setFieldSearch] = useState<string>("");

    const [formFilters, setFormFilters] = useState<Set<string>>(() => new Set(userSession.user?.userGroups.toIdList("id")));
    const [formSearch, setFormSearch] = useState<string>("");

    const [cloneForm] = useCloneFormMutation();

    const [shouldShowTemplates, setShowTemplates] = useState<boolean>(false);

    const tags = useMemo(() => data?.templates
        .flatMap(row => row.tags)
        .filterNull()
        .dedupe()
        .map(row => ({ id: row, name: row })), [data?.templates]);

    const jobFunctions = useMemo(() => data?.templates
        .flatMap(row => row.jobFunctions)
        .filterNull()
        .dedupe()
        .map(row => ({ id: row, name: JOB_FUNCTION_NAME[row] })), [data?.templates]);

    if (isLoading) {
        return <Loading />;
    }
    if (error || !data) {
        return <ErrorRemote error={error} />;
    }

    let { formFields, forms } = data;
    if (fieldFilters.size > 0 && data.userGroups.isNotEmpty) {
        formFields = formFields?.filter(row => row.userGroupIds?.isEmpty || row.userGroupIds?.some(id => fieldFilters.has(id)));
    }
    if (fieldSearch) {
        const search = fieldSearch.toLocaleLowerCase();
        formFields = formFields?.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description?.toLocaleLowerCase().includes(search));
    }

    if (formFilters.size > 0 && data.userGroups.isNotEmpty) {
        forms = forms?.filter(row => row.userGroupIds?.isEmpty || row.userGroupIds?.some(id => formFilters.has(id)));
    }
    if (formSearch) {
        const search = formSearch.toLocaleLowerCase();
        forms = forms?.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description?.toLocaleLowerCase().includes(search));
    }

    const saveAndClone = async (formId: string) => {
        const created = await cloneForm({
            refetchQueries: [GetRequestTypesDocument],
            variables: {
                id: formId,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
            id: created.data?.cloneForm.id,
            setting: "request-forms",
        });
        setShowTemplates(false);
    };

    return (
        <>
            <ListWithGroups
                title={(
                    <span>
                        <TbTemplate />
                        {" "}
                        Templates
                    </span>
                )}
                items={data.templates}
                shouldShow={shouldShowTemplates}
                onHide={() => setShowTemplates(false)}
                getKey={row => row.id}
                groups={[
                    jobFunctions?.isNotEmpty
                        ? {
                            id: "job-functions",
                            title: "Job Functions",
                            groups: jobFunctions,
                        } : undefined,
                    tags?.isNotEmpty
                        ? {
                            id: "tags",
                            title: "Tags",
                            groups: tags,
                        } : undefined,
                ].filterNull()}
                onSelected={item => saveAndClone(item?.id)}
            >
                {(template, search, selectedGroup) => (
                    (template.name.toLocaleLowerCase().includes(search)
                    || template.description?.toLocaleLowerCase().includes(search))
                    && (!selectedGroup
                            || template.jobFunctions?.find(job => selectedGroup?.itemId === job)
                            || template.tags?.find(job => selectedGroup?.itemId === job))
                        ? (
                            <div className="w-100 d-flex">
                                <div className="d-flex col-4 align-items-center">
                                    <div>
                                        <Avatar variant="xs" src={template.iconUrl as string} />
                                    </div>
                                    <h6 className="mb-0">
                                        {template.name}
                                    </h6>

                                </div>

                                <small className="col">{template.description}</small>

                            </div>
                        ) : null)}
            </ListWithGroups>
            <div className="d-flex align-items-stretch">
                <div className="border p-3 rounded bg-white col me-2">
                    {data.formFields?.isNotEmpty ? (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="text-light mb-0">
                                    Custom Fields
                                </h5>

                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: "new",
                                        setting: "request-fields",
                                    }}
                                    className="btn btn-none btn-sm"
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Custom Field
                                </Link>

                            </div>
                            <Filters
                                groups={data.userGroups}
                                selectedGroups={fieldFilters}
                                onSelected={setFieldFilters}
                                search={fieldSearch}
                                onSetSearch={setFieldSearch}
                            />
                            {formFields.isNotEmpty ? (
                                <div className="list-group">
                                    {formFields?.sortBy(row => row.name.toLowerCase()).map(field => (
                                        <Link
                                            key={field.id}
                                            to={Paths.ADMINISTRATOR_SETTING_ID}
                                            params={{
                                                id: field.id,
                                                setting: "request-fields",
                                            }}
                                            className="list-group-item list-group-item-action flex-column align-items-start"
                                        >
                                            <div className="d-flex w-100 align-items-end mb-2">
                                                <h5 className="mb-0">
                                                    {field.name}
                                                    {" "}
                                                </h5>

                                                <small className="ms-3 text-light ">
                                                    {CUSTOM_FIELD_PROPS[field.type]?.text ?? ""}
                                                </small>
                                            </div>
                                            <small>{field.description}</small>
                                        </Link>

                                    ))}

                                </div>
                            ) : (
                                <div className="text-center text-light">No custom fields found matching your filters.</div>
                            )}
                            <div className="d-flex justify-content-end align-items-center mt-3">

                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: "new",
                                        setting: "request-fields",
                                    }}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Custom Field
                                </Link>
                            </div>

                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                            <h5 className="text-light">
                                Custom Fields
                            </h5>
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                params={{
                                    id: "new",
                                    setting: "request-fields",
                                }}
                                className="btn btn-secondary btn-sm my-3"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Add Custom Field
                            </Link>
                            <small>
                                Custom fields allow you to associate additional types of data with your tickets.
                                For example, lets say you want to track the difficulty level of each request, you
                                can create a Difficulty Type custom field, associate it with a Request Type, enter the
                                difficulty level for each ticket the Tickets view, and create a report for each of the types.
                            </small>
                        </div>

                    )}

                </div>
                <div className="border p-3 rounded  bg-white col ms-2">
                    {data.forms?.isNotEmpty ? (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="text-light mb-0 flex-grow-1">
                                    Request Types
                                </h5>
                                {featureFlags.has(FEATURE_TEMPLATES) && (
                                    <button
                                        className="btn btn-none btn-sm me-3 border rounded px-2 py-1"
                                        type="button"
                                        onClick={() => setShowTemplates(true)}
                                    >
                                        <TbTemplate />
                                    </button>
                                )}

                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: "new",
                                        setting: "request-forms",
                                    }}
                                    className="btn btn-none btn-sm"
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Request Type
                                </Link>

                            </div>
                            <Filters
                                groups={data.userGroups}
                                selectedGroups={formFilters}
                                onSelected={setFormFilters}
                                search={formSearch}
                                onSetSearch={setFormSearch}
                            />
                            {forms.isNotEmpty ? (

                                <div className="list-group">
                                    {forms?.map(field => (
                                        <Link
                                            to={Paths.ADMINISTRATOR_SETTING_ID}
                                            key={field.id}
                                            params={{
                                                id: field.id,
                                                setting: "request-forms",
                                            }}
                                            className="list-group-item list-group-item-action flex-column align-items-start"
                                        >
                                            <div className="d-flex w-100 align-items-end mb-2">
                                                <h5 className="mb-0">
                                                    {field.name}
                                                    {" "}
                                                </h5>

                                                <small className="ms-3 text-light " />
                                            </div>
                                            <small>{field.description}</small>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-light">No request types found matching your filters.</div>
                            )}
                            <div className="d-flex justify-content-end align-items-center mt-3">
                                {featureFlags.has(FEATURE_TEMPLATES) && (

                                    <button
                                        className="btn rounded border btn-sm me-2"
                                        type="button"
                                        onClick={() => setShowTemplates(true)}
                                    >
                                        <TbTemplate />
                                        {" "}
                                        Templates
                                    </button>
                                )}
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: "new",
                                        setting: "request-forms",
                                    }}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Request Type
                                </Link>

                            </div>

                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                            <h5 className="text-light">
                                Request Types
                            </h5>
                            <div className="d-flex">
                                {featureFlags.has(FEATURE_TEMPLATES) && (

                                    <button
                                        type="button"
                                        className="btn border btn-sm my-3 me-3"
                                        onClick={() => setShowTemplates(true)}
                                    >
                                        <TbTemplate />
                                        {" "}
                                        Templates
                                    </button>
                                )}
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: "new",
                                        setting: "request-forms",
                                    }}
                                    className="btn btn-secondary btn-sm my-3"
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Add Request Types
                                </Link>

                            </div>
                            <small>
                                Request types allow you to automate your workflow. Imagine your customers are always
                                asking for access to a certain product or feature. Create a request type which automatically
                                asks users which product or feature they would like access to, triggers an approval workflow
                                from their manager, and once approved automatically initiates a workflow through an integration.
                                Add a request type to see how.
                            </small>
                        </div>

                    )}

                </div>

            </div>
        </>
    );
}
